/**
 * shared/hooks/useWizardNavigation.ts
 * Generic wizard navigation hook
 * 
 * Provides reusable wizard functionality that can be used across different wizard implementations:
 * - Step management and navigation
 * - Progress calculation
 * - Conditional step filtering
 * - Generic validation framework
 * 
 * This shared hook handles the common wizard patterns, while specific implementations
 * provide their own step definitions and validation logic.
 */

// 1. External dependencies
import { useState, useCallback, useMemo } from 'react';

/**
 * Generic step configuration interface
 */
export interface WizardStep<TData = Record<string, unknown>> {
  id: string;
  name: string;
  title: string;
  description?: string;
  condition?: (data?: TData) => boolean;
  validation?: (data?: TData) => boolean;
  excludeFromProgress?: boolean; // Don't count this step in progress calculation
}

/**
 * Wizard navigation hook configuration
 */
export interface UseWizardNavigationConfig<TData = Record<string, unknown>> {
  steps: WizardStep<TData>[];
  data?: TData;
  initialStepIndex?: number;
}

/**
 * Wizard navigation hook return interface
 */
export interface UseWizardNavigationReturn<TData = Record<string, unknown>> {
  // Current state
  currentStepIndex: number;
  currentStep: WizardStep<TData>;
  activeSteps: WizardStep<TData>[];
  totalSteps: number;
  progress: number;
  totalProgressSteps: number; // Count of steps that contribute to progress

  // Step validation
  isCurrentStepValid: boolean;
  canNavigateNext: boolean;
  canNavigateBack: boolean;

  // Navigation actions
  goToStep: (stepIndex: number) => void;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;

  // Utilities
  getStepValidation: (stepId: string) => boolean;
  isStepCompleted: (stepIndex: number) => boolean;
  getStepById: (stepId: string) => WizardStep<TData> | undefined;
}

/**
 * Generic wizard navigation hook
 * 
 * @param config - Configuration object with steps, data, and initial step
 * @returns Wizard navigation state and methods
 */
export const useWizardNavigation = <TData = Record<string, unknown>>({
  steps,
  data,
  initialStepIndex = 0
}: UseWizardNavigationConfig<TData>): UseWizardNavigationReturn<TData> => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);

  // Get active steps based on data and conditions
  const activeSteps = useMemo((): WizardStep<TData>[] => {
    return steps.filter(step => !step.condition || step.condition(data));
  }, [steps, data]);

  // Current step info with proper fallback
  const currentStep = useMemo(() => {
    return activeSteps[currentStepIndex] || activeSteps[0] || steps[0] || {
      id: 'default',
      name: 'Default',
      title: 'Default Step'
    };
  }, [activeSteps, currentStepIndex, steps]);
  const totalSteps = activeSteps.length;
  
  // Progress calculation (percentage) - exclude steps marked as excludeFromProgress
  const progressSteps = activeSteps.filter(step => !step.excludeFromProgress);
  const totalProgressSteps = progressSteps.length;

  // Calculate progress based on non-excluded steps only
  const currentProgressIndex = useMemo((): number => {
    // Count how many progress-counted steps we've passed (including current if it counts)
    let progressIndex = 0;
    for (let i = 0; i <= currentStepIndex && i < activeSteps.length; i++) {
      const step = activeSteps[i];
      if (step && !step.excludeFromProgress) {
        progressIndex++;
      }
    }
    return progressIndex;
  }, [activeSteps, currentStepIndex]);

  const progress = totalProgressSteps > 0 ? (currentProgressIndex / totalProgressSteps) * 100 : 0;

  // Step validation
  const isCurrentStepValid = useMemo((): boolean => {
    if (!currentStep?.validation) return true;
    return currentStep.validation(data);
  }, [currentStep, data]);

  const canNavigateNext = currentStepIndex < totalSteps - 1 && isCurrentStepValid;
  const canNavigateBack = currentStepIndex > 0;
  
  // Navigation actions
  const goToStep = useCallback((stepIndex: number): void => {
    if (stepIndex >= 0 && stepIndex < totalSteps) {
      setCurrentStepIndex(stepIndex);
    }
  }, [totalSteps]);

  const goNext = useCallback((): void => {
    if (canNavigateNext) {
      setCurrentStepIndex(prev => prev + 1);
    }
  }, [canNavigateNext]);

  const goBack = useCallback((): void => {
    if (canNavigateBack) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [canNavigateBack]);
  
  const reset = useCallback((): void => {
    setCurrentStepIndex(initialStepIndex);
  }, [initialStepIndex]);
  
  // Utilities
  const getStepValidation = useCallback((stepId: string): boolean => {
    const step = activeSteps.find(s => s.id === stepId);
    if (!step?.validation) return true;
    return step.validation(data);
  }, [activeSteps, data]);

  const isStepCompleted = useCallback((stepIndex: number): boolean => {
    const step = activeSteps[stepIndex];
    if (!step) return false;
    if (!step.validation) return stepIndex < currentStepIndex;
    return step.validation(data);
  }, [activeSteps, currentStepIndex, data]);

  const getStepById = useCallback((stepId: string): WizardStep<TData> | undefined => {
    return activeSteps.find(step => step.id === stepId);
  }, [activeSteps]);

  return {
    // Current state
    currentStepIndex,
    currentStep,
    activeSteps,
    totalSteps,
    progress,
    totalProgressSteps,

    // Step validation
    isCurrentStepValid,
    canNavigateNext,
    canNavigateBack,

    // Navigation actions
    goToStep,
    goNext,
    goBack,
    reset,

    // Utilities
    getStepValidation,
    isStepCompleted,
    getStepById
  };
};